import { useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useRpcMutation, useRpcQuery } from "../../runtime/hooks/useRpc";
import type {
  ExtensionFilter,
  ExtensionListResponse,
  ExtensionView,
} from "./types";

const LIST_KEY = ["extensions", "list"] as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function useExtensionsList(filter?: ExtensionFilter) {
  const baseUrl = useAppRuntime().transport.baseUrl;
  const query = useRpcQuery<ExtensionListResponse>(
    [...LIST_KEY, filter ?? null],
    "extensions.list",
    (filter as Record<string, unknown> | undefined) ?? undefined,
  );
  // The backend returns `icon_url` ROOT-RELATIVE (`/api/plugins/<id>/icon.svg`).
  // A bare path resolves against the PAGE origin: identical to the backend in
  // the browser, but `tauri://localhost` in the desktop webview — where it
  // points at nothing and every extension icon silently disappears. Absolutise
  // it here, once, where the data arrives, so no view or call site has to know.
  const data = useMemo(() => {
    const raw = query.data;
    if (raw === undefined) return raw;
    return {
      ...raw,
      extensions: raw.extensions.map((e) =>
        // `icon_url` is typed non-nullable but the wire sends null for remote
        // catalog entries, so this must be a runtime check, not a type-level
        // one: calling startsWith on null throws and takes the whole rail —
        // icons AND their drag-to-reorder — down with it.
        typeof e.icon_url === "string" && e.icon_url.startsWith("/")
          ? { ...e, icon_url: `${baseUrl}${e.icon_url}` }
          : e,
      ),
    };
  }, [query.data, baseUrl]);
  return { ...query, data };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function useSetExtensionEnabled() {
  const qc = useQueryClient();
  return useRpcMutation<ExtensionView, { key: string; enabled: boolean }>(
    "extensions.set_enabled",
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: LIST_KEY });
      },
    },
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function useSetExtensionPosition() {
  const qc = useQueryClient();
  return useRpcMutation<ExtensionView, { key: string; position: number }>(
    "extensions.set_position",
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: LIST_KEY });
      },
    },
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function useInstallSkill() {
  const qc = useQueryClient();
  return useRpcMutation<ExtensionView, { key: string }>("extensions.install", {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function useUninstallSkill() {
  const qc = useQueryClient();
  return useRpcMutation<{ ok: boolean }, { key: string }>(
    "extensions.uninstall",
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: LIST_KEY });
      },
    },
  );
}
